// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useLatestRef } from '@chakra-ui/hooks';
import {
  AlertStatus,
  Box,
  createStandaloneToast,
  Flex,
  ToastId,
  toastStore,
  UseToastOptions,
  useMultiStyleConfig,
  Text,
} from '@chakra-ui/react';
import { ComponentMultiStyleConfig } from '@chakra-ui/theme';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle';
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faCircleExclamation';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { ReactNode, useMemo } from 'react';

import { QuotientIcon } from '../Icon/Icon';
import { QuotientIconButton } from '../IconButton/IconButton';

export const ToastStyle: ComponentMultiStyleConfig = {
  parts: ['container', 'icon'],
  baseStyle: {
    container: {
      minWidth: 'auto',
      width: 'auto',
      display: 'flex',
      flexDirection: 'row',
      borderRadius: '4px',
      padding: '16px',
      gap: '12px',
      justifyContent: 'space-between',
      alignItems: 'center',
      shadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.1)',
    },
  },
  variants: {
    success: {
      container: {
        backgroundColor: 'positive.50',
      },
      icon: {
        color: 'positive.600',
      },
    },
    error: {
      container: {
        backgroundColor: 'danger.50',
      },
      icon: {
        color: 'danger.600',
      },
    },
    warning: {
      container: {
        backgroundColor: 'warning.50',
      },
      icon: {
        color: 'warning.600',
      },
    },
    info: {
      container: {
        backgroundColor: 'primary.100',
      },
      icon: {
        color: 'primary.600',
      },
    },
  },
};

export const iconMapping: Record<Exclude<AlertStatus, 'loading'>, IconDefinition> = {
  error: faCircleExclamation,
  success: faCheckCircle,
  warning: faCircleExclamation,
  info: faCircleInfo,
};

export type ToastProp = {
  status?: AlertStatus;
  id?: ToastId;
  title?: ReactNode;
  isClosable?: boolean;
  onClose?: () => void;
  description?: ReactNode;
};

export const Toast = ({ status = 'success', id, title, isClosable, onClose, description }: ToastProp) => {
  const styles = useMultiStyleConfig('Toast', { variant: status });
  const alertTitleId = typeof id !== 'undefined' ? `toast-${id}-title` : undefined;

  const iconToUse = status === 'loading' ? iconMapping.info : iconMapping[status as Exclude<AlertStatus, 'loading'>];

  return (
    <Flex __css={styles.container}>
      <QuotientIcon
        alignSelf="flex-start"
        color={(styles?.icon?.color as string) || 'positive.600'}
        icon={iconToUse}
        mt={1}
      />
      <Box>
        {title ? (
          <Text id={alertTitleId} m={0} textStyle="bodySemiBold">
            {title}
          </Text>
        ) : null}
        {description ? (
          <Text m={0} textStyle="bodyRegular">
            {description}
          </Text>
        ) : null}
      </Box>
      {isClosable ? (
        <Box alignSelf="flex-start" mt={-2}>
          <QuotientIconButton icon={faXmark} unmask onClick={onClose} />
        </Box>
      ) : null}
    </Flex>
  );
};

const defaults = {
  duration: 5000,
  isClosable: true,
  variant: 'subtle',
  position: 'top-right',
  containerStyle: {
    minWidth: 'auto',
  },
} as const;

// Chakra v2's `toastStore.subscribe` has buggy cleanup: when any listener unsubscribes, it resets
// the entire store to its initial (empty) state instead of just removing that listener. Our app
// uses nested <ChakraProvider>s (one per <ChakraWrapper isEnabled>), so every drawer/modal that
// opens mounts a new ToastProvider, and every close unmounts it — wiping the store each time.
// A toast fired right before a drawer close (common pattern: "saved successfully" → close)
// gets nuked before it's visible for its full duration.
//
// Replace the cleanup with a no-op so the store survives nested-provider churn. The orphan
// listeners are harmless (React ignores state updates for unmounted components), and the
// always-mounted <Sidebar>'s ToastProvider remains subscribed to render queued toasts.
const originalSubscribe = toastStore.subscribe;
toastStore.subscribe = (listener: () => void) => {
  originalSubscribe(listener);
  return () => {};
};

const { toast: standaloneToast } = createStandaloneToast({ defaultOptions: defaults });

// Manual store reset for test setup. Only needed because the patch above disables the built-in
// auto-reset; without it, Chakra would clear the store for us when the last ToastProvider
// unmounts between tests. Call from `beforeEach` in test setup to keep toasts from leaking.
// eslint-disable-next-line @typescript-eslint/naming-convention
export const __resetToastStore = () => {
  Object.values(toastStore.getState())
    .flat()
    .forEach((toast) => toastStore.removeToast(String(toast.id), toast.position));
};

export const useQuotientToast = (options?: UseToastOptions) => {
  const toastOptions = useLatestRef(options);

  return useMemo(() => {
    const showToast = (opts?: UseToastOptions) => {
      return standaloneToast({
        ...defaults,
        ...toastOptions.current,
        ...opts,
        render: (props) => <Toast {...props} {...opts} />,
      });
    };

    showToast.close = standaloneToast.close;
    showToast.closeAll = standaloneToast.closeAll;
    showToast.update = standaloneToast.update;
    showToast.isActive = standaloneToast.isActive;

    return showToast;
  }, [toastOptions]);
};
